import {
  ArrowPathIcon,
  DocumentIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { Dropzone } from '@/components/Dropzone';
import { cn } from '@/lib/utils';

/** Explicit four-state machine for the eager-upload flow. */
export type DocumentUploadState = 'idle' | 'uploading' | 'uploaded' | 'error';

interface DocumentUploadZoneProps {
  onFilesDrop: (files: { content: string | ArrayBuffer; file: File }[]) => void;
  /**
   * Determinate progress 0-100, or null when not uploading. Back-compat: when
   * `state` is omitted, this drives the legacy two-view behavior (null ⇒
   * dropzone, number ⇒ progress bar) so existing callers are unchanged.
   */
  uploadProgress: number | null;
  /**
   * Drives which sub-view renders (idle / uploading / uploaded / error). When
   * omitted, the component falls back to the legacy `uploadProgress` behavior.
   */
  state?: DocumentUploadState;
  /** File name shown in the uploading / uploaded / error rows. */
  fileName?: string;
  /** Optional human-readable size label (e.g. "5.1 MB"). */
  fileSizeLabel?: string;
  /** Shown in the error row. */
  errorMessage?: string;
  /** uploading: abort the in-flight upload. */
  onCancel?: () => void;
  /** error: re-upload the same key. */
  onRetry?: () => void;
  /** uploaded/error: clear back to idle (triggers the rollback delete). */
  onRemove?: () => void;
  title?: string;
  subTitle?: string;
  ariaLabel?: string;
}

/**
 * Generic file upload surface. Two modes:
 *
 *  - **Legacy (back-compat):** omit `state` and pass `uploadProgress:
 *    number|null` — renders the dropzone (null) or a determinate progress bar
 *    (number), exactly as before.
 *  - **State machine:** pass an explicit `state` (idle/uploading/uploaded/
 *    error) for the eager-upload wizard. `uploading` shows a file row with a
 *    determinate (or indeterminate when `uploadProgress` is null) bar + Cancel;
 *    `uploaded` shows a neutral selected-file row (no success/green affordance);
 *    `error` shows an alert row with Retry + Remove.
 */
export const DocumentUploadZone: React.FC<DocumentUploadZoneProps> = ({
  onFilesDrop,
  uploadProgress,
  state,
  fileName,
  fileSizeLabel,
  errorMessage,
  onCancel,
  onRetry,
  onRemove,
  title = 'Click or drag and drop files here',
  subTitle,
  ariaLabel,
}) => {
  const dropzone = (
    <Dropzone
      type="arrayBuffer"
      onFilesDrop={onFilesDrop}
      title={title}
      subTitle={subTitle}
      aria-label={ariaLabel}
    />
  );

  // --- Back-compat path: no explicit state -------------------------------
  // null ⇒ dropzone, number ⇒ legacy progress bar. Preserves the original
  // contract for every existing caller (profile pictures, imports, etc.).
  if (state === undefined) {
    if (uploadProgress !== null) {
      return (
        <div className="w-full">
          <div className="relative pt-1">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-chariot-text-primary text-xs font-semibold uppercase">
                Uploading...
              </span>
              <span className="text-brand text-xs font-semibold">
                {Math.round(uploadProgress)}%
              </span>
            </div>
            <div className="bg-chariot-background-tertiary h-2 overflow-hidden rounded">
              <div
                style={{ width: `${uploadProgress}%` }}
                className="bg-brand h-full transition-all duration-300"
              ></div>
            </div>
          </div>
        </div>
      );
    }
    return dropzone;
  }

  // --- State-machine path ------------------------------------------------
  if (state === 'idle') {
    return dropzone;
  }

  const name = fileName ?? 'File';

  if (state === 'uploading') {
    const hasProgress = uploadProgress !== null;
    const pct = hasProgress ? Math.round(uploadProgress) : undefined;
    return (
      <div className="border-chariot-stroke flex flex-col gap-3 rounded-xl border p-4">
        <div className="flex items-center gap-3">
          <span className="bg-chariot-background-tertiary text-chariot-text-secondary flex size-9 shrink-0 items-center justify-center rounded-md">
            <DocumentIcon className="size-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="text-chariot-text-primary block truncate text-sm font-semibold">
              {name}
            </span>
            <span className="text-chariot-text-secondary block text-xs">
              {hasProgress ? `Uploading… ${pct}%` : `Uploading ${name}…`}
            </span>
          </span>
          <span className="ml-auto flex shrink-0 items-center gap-2">
            {pct !== undefined && (
              <span className="text-brand text-xs font-semibold tabular-nums">
                {pct}%
              </span>
            )}
            {onCancel && (
              <button
                type="button"
                aria-label="Cancel upload"
                onClick={onCancel}
                className="border-chariot-stroke text-chariot-text-secondary hover:text-chariot-text-primary focus-visible:ring-chariot-brand-primary flex size-7 items-center justify-center rounded-md border outline-hidden transition-colors focus-visible:ring-2"
              >
                <XMarkIcon className="size-4" />
              </button>
            )}
          </span>
        </div>
        <div
          role="progressbar"
          aria-label="Upload progress"
          {...(hasProgress
            ? { 'aria-valuenow': pct, 'aria-valuemin': 0, 'aria-valuemax': 100 }
            : {})}
          className="bg-chariot-background-tertiary h-1.5 overflow-hidden rounded-full"
        >
          <div
            style={hasProgress ? { width: `${uploadProgress}%` } : undefined}
            className={cn(
              'bg-brand h-full',
              hasProgress
                ? 'transition-all duration-300 motion-reduce:transition-none'
                : 'w-2/5 motion-safe:animate-pulse'
            )}
          ></div>
        </div>
        <span className="sr-only" aria-live="polite">
          {hasProgress
            ? `Uploading ${name}, ${pct} percent.`
            : `Uploading ${name}.`}
        </span>
      </div>
    );
  }

  if (state === 'uploaded') {
    return (
      <div className="border-chariot-stroke flex items-center gap-3 rounded-xl border p-4">
        <span className="bg-chariot-background-tertiary text-chariot-text-secondary flex size-9 shrink-0 items-center justify-center rounded-md">
          <DocumentIcon className="size-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="text-chariot-text-primary block truncate text-sm font-semibold">
            {name}
          </span>
          {fileSizeLabel && (
            <span className="text-chariot-text-secondary block text-xs">
              {fileSizeLabel}
            </span>
          )}
        </span>
        {onRemove && (
          <button
            type="button"
            aria-label="Remove file"
            onClick={onRemove}
            className="border-chariot-stroke text-chariot-text-secondary hover:text-chariot-text-primary focus-visible:ring-chariot-brand-primary flex size-7 shrink-0 items-center justify-center rounded-md border outline-hidden transition-colors focus-visible:ring-2"
          >
            <XMarkIcon className="size-4" />
          </button>
        )}
        <span className="sr-only" aria-live="polite">
          {name} selected.
        </span>
      </div>
    );
  }

  // state === 'error'
  return (
    <div
      role="alert"
      className="border-error bg-chariot-brand-tertiary flex flex-col gap-3 rounded-xl border p-4"
    >
      <div className="flex items-center gap-3">
        <span className="bg-error-light text-error flex size-9 shrink-0 items-center justify-center rounded-md">
          <ExclamationCircleIcon className="size-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="text-chariot-text-primary block truncate text-sm font-semibold">
            {name}
          </span>
          <span className="text-error block text-xs">
            {errorMessage ?? 'Upload failed. Drop the file again to retry.'}
          </span>
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-2">
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="text-brand-light focus-visible:ring-chariot-brand-primary flex items-center gap-1 rounded-md px-2 py-1 text-sm font-semibold outline-hidden hover:underline focus-visible:ring-2"
            >
              <ArrowPathIcon className="size-4" />
              Retry
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              aria-label="Remove file"
              onClick={onRemove}
              className="border-chariot-stroke text-chariot-text-secondary hover:text-chariot-text-primary focus-visible:ring-chariot-brand-primary flex size-7 items-center justify-center rounded-md border outline-hidden transition-colors focus-visible:ring-2"
            >
              <XMarkIcon className="size-4" />
            </button>
          )}
        </span>
      </div>
    </div>
  );
};
